import { isDevMode } from '../parcels/utils/is-dev-mode';

/** localStorage key holding the dev-only feature-flag override map. */
export const FEATURE_FLAG_OVERRIDE_STORAGE_KEY = 'abax:feature-flag-overrides';

/** URL query param carrying a shareable override set (`name:0/1;name:0/1`). */
export const FEATURE_FLAG_OVERRIDE_URL_PARAM = 'ff_override';

function isOverrideMap(value: unknown): value is Record<string, boolean> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.entries(value).every(([k, v]) => typeof k === 'string' && typeof v === 'boolean')
  );
}

/** Parse the `name:0/1;name:0/1` shareable-link format (inverse of {@link generateShareableUrl}). */
function parseOverrideParam(value: string): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const pair of value.split(';')) {
    const idx = pair.lastIndexOf(':');
    if (idx <= 0) continue;
    const name = pair.slice(0, idx).trim();
    const flag = pair.slice(idx + 1).trim();
    if (name && (flag === '0' || flag === '1')) out[name] = flag === '1';
  }
  return out;
}

/** Read the shareable-link overrides from the current URL, or an empty map. */
function readUrlOverrides(): Record<string, boolean> {
  try {
    const param = new URLSearchParams(window.location.search).get(FEATURE_FLAG_OVERRIDE_URL_PARAM);
    return param ? parseOverrideParam(param) : {};
  } catch {
    return {};
  }
}

/**
 * Remove `name` (or all entries when `name` is omitted) from the `ff_override`
 * URL param. Because `listOverrides()` re-merges the URL on every read, an
 * explicit clear must also drop the URL-seeded entry or the next read would
 * re-apply it from the address bar.
 */
function stripUrlOverride(name?: string): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(FEATURE_FLAG_OVERRIDE_URL_PARAM)) return;
    const remaining =
      name === undefined
        ? {}
        : Object.fromEntries(Object.entries(readUrlOverrides()).filter(([key]) => key !== name));
    const value = Object.entries(remaining)
      .map(([key, enabled]) => `${key}:${enabled ? '1' : '0'}`)
      .join(';');
    if (value) url.searchParams.set(FEATURE_FLAG_OVERRIDE_URL_PARAM, value);
    else url.searchParams.delete(FEATURE_FLAG_OVERRIDE_URL_PARAM);
    window.history.replaceState(window.history.state, '', url);
  } catch {
    /* ignore */
  }
}

/**
 * Read all active overrides. Dev-only and browser-only: returns an empty map in
 * production or where `window`/`localStorage` are absent, so the store can never
 * influence a production resolution.
 */
export function listOverrides(): Record<string, boolean> {
  if (!isDevMode()) return {};
  if (typeof window === 'undefined') return {};

  let stored: Record<string, boolean> = {};
  try {
    const raw = localStorage.getItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isOverrideMap(parsed)) {
        stored = parsed;
      } else if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn(
          '[feature-flags] localStorage overrides have invalid shape (expected Record<string, boolean>)',
        );
      }
    }
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.warn(
        '[feature-flags] Failed to parse localStorage overrides:',
        err instanceof Error ? err.message : err,
      );
    }
  }

  // A shareable `ff_override` link wins over localStorage, mirroring the parcel
  // override resolution — so a copied link applies on load (the round-trip with
  // generateShareableUrl), without a separate boot-time parse step.
  const urlOverrides = readUrlOverrides();
  return Object.keys(urlOverrides).length > 0 ? { ...stored, ...urlOverrides } : stored;
}

/** Read a single override, or `undefined` when no override is set for `name`. */
export function getOverride(name: string): boolean | undefined {
  const overrides = listOverrides();
  return Object.prototype.hasOwnProperty.call(overrides, name) ? overrides[name] : undefined;
}

type OverridesListener = () => void;

interface OverridesListenerRegistry {
  listeners: Set<OverridesListener>;
  crossTabHookInstalled: boolean;
}

/**
 * Window-anchored, like every other registry in this package: the platform is
 * NOT a Module Federation singleton, so the shell and each MFE run their own
 * copy of this module in one document. A module-local set would only deliver
 * same-copy notifications — the DevTools panel (shell copy) flipping an
 * override would never reach an MFE copy's `useFeatureGate` (the `storage`
 * event fires cross-document only, not same-document).
 */
const OVERRIDE_LISTENERS_KEY = '__ABAX_FF_OVERRIDE_LISTENERS__';
let fallbackListenerRegistry: OverridesListenerRegistry | undefined;

function createListenerRegistry(): OverridesListenerRegistry {
  return { listeners: new Set(), crossTabHookInstalled: false };
}

function getListenerRegistry(): OverridesListenerRegistry {
  if (typeof window === 'undefined') {
    fallbackListenerRegistry ??= createListenerRegistry();
    return fallbackListenerRegistry;
  }
  const w = window as unknown as Record<string, OverridesListenerRegistry | undefined>;
  w[OVERRIDE_LISTENERS_KEY] ??= createListenerRegistry();
  return w[OVERRIDE_LISTENERS_KEY] as OverridesListenerRegistry;
}

function notifyOverridesChanged(): void {
  const registry = getListenerRegistry();
  for (const listener of [...registry.listeners]) listener();
}

/**
 * Subscribe to override changes. Fires on every same-document `setOverride` /
 * `clearOverride` / `clearAllOverrides` (from ANY platform copy) and, once any
 * subscriber exists, on cross-tab writes via the `storage` event. Without
 * this channel an override flip only takes effect on the next full page load
 * — live consumers (`useFeatureGate`) re-resolve through it instead.
 *
 * @returns An unsubscribe function.
 */
export function onOverridesChange(listener: OverridesListener): () => void {
  const registry = getListenerRegistry();
  if (!registry.crossTabHookInstalled && typeof window !== 'undefined') {
    registry.crossTabHookInstalled = true;
    window.addEventListener('storage', (event) => {
      if (event.key === FEATURE_FLAG_OVERRIDE_STORAGE_KEY) notifyOverridesChanged();
    });
  }
  registry.listeners.add(listener);
  return () => {
    registry.listeners.delete(listener);
  };
}

/** Set (or replace) the override for `name`. No-op outside dev / outside the browser. */
export function setOverride(name: string, value: boolean): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;

  const current = listOverrides();
  current[name] = value;
  localStorage.setItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY, JSON.stringify(current));
  // The URL wins on every read, so a URL-seeded entry would mask this write.
  if (Object.prototype.hasOwnProperty.call(readUrlOverrides(), name)) stripUrlOverride(name);
  notifyOverridesChanged();
}

/** Remove the override for `name`. Clears the storage key when none remain. */
export function clearOverride(name: string): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;

  const current = listOverrides();
  delete current[name];

  if (Object.keys(current).length === 0) {
    localStorage.removeItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY);
  } else {
    localStorage.setItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY, JSON.stringify(current));
  }
  // Also drop it from the URL param, or the next read re-seeds it from the link.
  stripUrlOverride(name);
  notifyOverridesChanged();
}

/** Remove every override at once. No-op outside dev / outside the browser. */
export function clearAllOverrides(): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;

  localStorage.removeItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY);
  stripUrlOverride();
  notifyOverridesChanged();
}

/**
 * Build a shareable URL that re-applies the active (or supplied) overrides on load.
 * Returns the current href unchanged when there are no overrides. Browser-only.
 */
export function generateShareableUrl(overrides?: Record<string, boolean>): string {
  if (typeof window === 'undefined') return '';

  const effectiveOverrides = overrides ?? listOverrides();
  if (Object.keys(effectiveOverrides).length === 0) {
    return window.location.href;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(FEATURE_FLAG_OVERRIDE_URL_PARAM);

  const value = Object.entries(effectiveOverrides)
    .map(([name, enabled]) => `${name}:${enabled ? '1' : '0'}`)
    .join(';');
  url.searchParams.set(FEATURE_FLAG_OVERRIDE_URL_PARAM, value);

  return url.toString();
}
